import React, { useState, useEffect } from 'react';
import { getAllDeviceConfigs, pingDevice } from '../services/api';
import { Activity, Zap, Server, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';

const PingDevice = () => {
    const [deviceList, setDeviceList] = useState([]);
    const [selectedIp, setSelectedIp] = useState('');
    const [customIp, setCustomIp] = useState('');
    const [timeout, setTimeoutVal] = useState(10000);
    const [pingResult, setPingResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingDevices, setLoadingDevices] = useState(true);
    const [error, setError] = useState(null);
    const [useCustomIp, setUseCustomIp] = useState(false);

    useEffect(() => {
        const fetchDevices = async () => {
            try {
                const response = await getAllDeviceConfigs(1, 500);
                const list = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
                setDeviceList(list);
                if (list.length > 0) setSelectedIp(list[0].ip);
            } catch (err) {
                console.error("Failed to fetch devices", err);
            } finally {
                setLoadingDevices(false);
            }
        };
        fetchDevices();
    }, []);

    const handlePing = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setPingResult(null);

        const targetIp = useCustomIp ? customIp : selectedIp;
        if (!targetIp) {
            setError("Please select or enter an IP address.");
            setLoading(false);
            return;
        }

        try {
            const response = await pingDevice(targetIp, timeout);
            setPingResult(response?.data || response); 
        } catch (err) {
            console.error("Ping failed:", err);
            const msg = err.response?.data?.title || err.response?.data || err.message || "Ping failed.";
            setError(`Ping failed: ${msg}`);
        } finally {
            setLoading(false);
        }
    };

    if (loadingDevices) {
        return <div className="p-8 flex items-center justify-center text-gray-500">Loading resources...</div>;
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            {/* Header Section */}
            <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Ping Device</h1>
                <p className="text-sm text-gray-500 mt-1">Check connectivity and latency for a specific device.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                
                {/* Configuration Card */}
                <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-6">
                    <div className="flex items-center justify-between">
                         <h2 className="text-lg font-bold text-gray-800">Configuration</h2>
                         <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                            <button
                                type="button"
                                onClick={() => setUseCustomIp(false)}
                                className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-md transition-all ${!useCustomIp ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Saved Device
                            </button>
                            <button
                                type="button"
                                onClick={() => setUseCustomIp(true)}
                                className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-md transition-all ${useCustomIp ? 'bg-white shadow text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Custom IP
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handlePing} className="space-y-4">
                        {!useCustomIp ? (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Select Device</label>
                                <div className="relative group">
                                    <Server className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                    <select
                                        value={selectedIp}
                                        onChange={(e) => setSelectedIp(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all cursor-pointer appearance-none"
                                    >
                                        {Array.isArray(deviceList) && deviceList.map(dev => (
                                            <option key={dev.ip} value={dev.ip}>{dev.deviceName} ({dev.ip})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Enter IP Address</label>
                                <div className="relative group">
                                    <Zap className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                    <input
                                        type="text"
                                        value={customIp}
                                        onChange={(e) => setCustomIp(e.target.value)}
                                        placeholder="e.g., 192.168.1.100"
                                        className="w-full pl-9 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all"
                                    />
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Timeout (ms)</label>
                            <div className="relative group">
                                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                <input
                                    type="number"
                                    value={timeout}
                                    onChange={(e) => setTimeoutVal(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 bg-gray-50 hover:bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all"
                                />
                            </div>
                        </div>

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium text-sm transition-all shadow-sm ${loading
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow'
                                    }`}
                            >
                                {loading ? <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" /> : <Activity className="h-4 w-4" />}
                                {loading ? 'Pinging Device...' : 'Start Ping'}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Results Area */}
                <div className="space-y-6">
                    {/* Error State */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3 animate-in fade-in slide-in-from-top-2">
                             <div className="p-1.5 bg-red-100 rounded-full flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-red-600" />
                            </div>
                            <div className="text-sm text-red-800 pt-1">
                                <p className="font-bold mb-1">Ping Failed</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Success State */}
                    {pingResult && (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4">
                            <div className="bg-emerald-50/50 p-8 flex flex-col items-center justify-center border-b border-emerald-100">
                                <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mb-3 shadow-inner">
                                    <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Ping Successful</h3>
                                <p className="text-sm text-gray-500">Target device responded</p>
                            </div>
                            
                            <div className="grid grid-cols-2 divide-x divide-gray-100">
                                <div className="p-4 text-center">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Status</p>
                                    <p className="text-lg font-bold text-emerald-600 font-mono">{pingResult.status}</p>
                                </div>
                                <div className="p-4 text-center">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Latency</p>
                                    <p className="text-lg font-bold text-gray-900 font-mono">{pingResult.roundtripTime} ms</p>
                                </div>
                            </div>
                            <div className="bg-gray-50 p-3 text-center border-t border-gray-100">
                                <p className="text-xs text-gray-400 font-mono">
                                    Timestamp: {new Date(pingResult.timestamp).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Pending State */}
                    {!pingResult && !error && !loading && (
                        <div className="h-full min-h-[300px] bg-white rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-8 text-center">
                            <div className="p-4 bg-gray-50 rounded-full mb-3">
                                <Activity className="h-6 w-6 text-gray-300" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">Ready to Ping</h3>
                            <p className="text-sm text-gray-500 mt-1 max-w-[200px]">Configure the settings on the left and click "Start Ping"</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PingDevice;
